import { Link, useLocation } from 'react-router-dom';
import { FolderKanban, Home, ListTodo, LogOut, Mail, Menu, type LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuthStore, useAppStore } from '@/hooks/useStore';
import { getMobileRoutes } from '@/config/navigation';
import { useLogout } from '@/hooks/useLogout';

const primaryIcons: Record<string, LucideIcon> = {
  home: Home,
  myTasks: ListTodo,
  projects: FolderKanban,
  inbox: Mail,
  settings: Menu,
};

export default function MobileBottomNav() {
  const location = useLocation();
  const { user } = useAuthStore();
  const { sidebarOpen, notificationUnreadCount: unreadCount, closeTaskModal } = useAppStore();
  const logout = useLogout();
  const routes = getMobileRoutes(user?.role);
  const primaryDestination = routes.find((item) =>
    item.key === 'settings'
      ? false
      : item.path === '/'
        ? location.pathname === '/'
        : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
  );

  return (
    <nav
      aria-hidden={sidebarOpen || undefined}
      className={cn(
        'bottom-nav fixed bottom-0 left-0 right-0 z-50 lg:hidden',
        sidebarOpen && 'pointer-events-none invisible',
      )}
    >
      <div className="flex min-h-[72px] items-stretch justify-around px-1">
        {routes.map((item) => {
          const Icon = primaryIcons[item.key] ?? item.mobileIcon ?? item.icon;
          const isActive =
            primaryDestination?.key === item.key ||
            (item.key === 'settings' && !primaryDestination);

          return (
            <Link
              key={item.key}
              to={item.path}
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                'relative flex min-h-[72px] min-w-0 flex-1 flex-col items-center justify-start gap-1.5 px-1 pb-4 pt-3 transition-colors active:bg-primary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400',
                isActive ? 'text-primary-500' : 'text-gray-500',
              )}
              onClick={closeTaskModal}
            >
              <div className="relative">
                <Icon className="h-5 w-5 text-primary-600" strokeWidth={1.8} aria-hidden="true" />
                {item.key === 'inbox' && unreadCount > 0 && (
                  <span
                    aria-label={`${unreadCount} unread notifications`}
                    className="absolute -right-2 -top-1.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-primary-500 px-0.5 text-[9px] font-semibold text-white"
                  >
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <span className="text-[12px] leading-[18px]">{item.mobileLabel ?? item.label}</span>
              {isActive && (
                <span
                  className="absolute bottom-1.5 h-[3px] w-8 rounded-full bg-primary-500"
                  aria-hidden="true"
                />
              )}
            </Link>
          );
        })}
        {user?.role === 'guest' && (
          <button
            type="button"
            className="flex min-h-[72px] min-w-0 flex-1 flex-col items-center justify-start gap-1.5 px-1 pb-4 pt-3 text-gray-500 transition-colors active:bg-primary-50"
            onClick={() => {
              closeTaskModal();
              void logout();
            }}
          >
            <LogOut className="h-5 w-5 text-primary-600" strokeWidth={1.8} aria-hidden="true" />
            <span className="text-[12px] leading-[18px]">Sign out</span>
          </button>
        )}
      </div>
    </nav>
  );
}
